import { useEffect, useMemo, useRef, useState } from 'react';
import { getStoredUser } from '../utils/storage';
import { getCurrentRound, getCurrentRoundWithBets, getBetHistory } from '../services/api/gameApi';
import { connectSocket, disconnectSocket } from '../services/socket/socketClient';
import { registerSocketListeners, removeSocketListeners } from '../services/socket/socketListeners';

const isObject = (v) => v !== null && typeof v === 'object';
const BETTING_MS = 10000;
const TAKEOFF_MS = 2000;
const CRASH_HOLD_MS = 3500;
const BETTING_STATUSES = new Set(['BETTING','BETTING_OPEN','OPEN','OPEN_FOR_BETS','ACCEPTING_BETS','ACCEPTING_BETS_NOW','PLACE_BETS','BET_OPEN']);

const toTimestamp = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number' || /^\d+(\.\d+)?$/.test(String(value))) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return n < 1e12 ? n * 1000 : n;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const withBettingTiming = (round, now = Date.now()) => {
  if (!round) return round;
  const normalizedStatus = String(round.status || '').toUpperCase();
  if (!BETTING_STATUSES.has(normalizedStatus)) return round;
  round = { ...round, status: 'BETTING' };
  const explicitEnd = toTimestamp(round.bettingEndTime);
  const explicitStart = toTimestamp(round.startTime);

  if (explicitEnd) {
    const start = explicitStart || (explicitEnd - BETTING_MS);
    return { ...round, bettingEndTime: new Date(explicitEnd).toISOString(), startTime: new Date(start).toISOString(), remainingMilliseconds: Math.max(0, explicitEnd - now) };
  }

  const remaining = Number(round.remainingMilliseconds);
  const hasRemaining = Number.isFinite(remaining) && remaining > 0 && remaining <= BETTING_MS;
  const end = hasRemaining ? now + remaining : (explicitStart > now - 1000 ? explicitStart + BETTING_MS : now + BETTING_MS);
  const start = end - BETTING_MS;
  return { ...round, bettingEndTime: new Date(end).toISOString(), startTime: new Date(start).toISOString(), remainingMilliseconds: Math.max(0, end - now) };
};

const unwrap = (value) => {
  let x = value;
  for (let i = 0; i < 5; i += 1) {
    if (!isObject(x)) return x;
    if (isObject(x.data) && !('_id' in x) && !('roundNumber' in x) && !('status' in x)) x = x.data;
    else break;
  }
  return x;
};

const scalar = (value, fallback = null) => {
  if (value === null || value === undefined) return fallback;
  if (!isObject(value)) return value;
  if ('roundNumber' in value && !isObject(value.roundNumber)) return value.roundNumber;
  if ('value' in value && !isObject(value.value)) return value.value;
  if ('_id' in value && !('status' in value)) return value._id;
  return fallback;
};

export const normalizeRound = (raw) => {
  const x = unwrap(raw) || {};
  const nested = isObject(x.round) ? x.round : (isObject(x.currentRound) ? x.currentRound : null);
  const r = nested || x;
  const roundNumberValue = scalar(r.roundNumber, scalar(x.roundNumber, null));
  const statusValue = scalar(r.status, scalar(x.status, 'WAITING'));
  const multiplierValue = scalar(r.currentMultiplier, scalar(r.multiplier, scalar(x.currentMultiplier, scalar(x.multiplier, 1))));
  const crashValue = scalar(r.crashPoint, scalar(x.crashPoint, null));

  return {
    roundId: scalar(r._id, scalar(r.roundId, scalar(x.roundId, null))),
    roundNumber: roundNumberValue == null ? null : Number(roundNumberValue) || roundNumberValue,
    status: typeof statusValue === 'string' ? statusValue.toUpperCase() : 'WAITING',
    multiplier: Number(multiplierValue) || 1,
    crashPoint: crashValue == null ? null : Number(crashValue),
    bettingEndTime: r.bettingEndTime || x.bettingEndTime || null,
    startTime: r.startTime || x.startTime || null,
    flightStartTime: r.flightStartTime || x.flightStartTime || null,
    crashTime: r.crashTime || x.crashTime || null,
    remainingMilliseconds: Number(r.remainingMilliseconds ?? x.remainingMilliseconds ?? 0) || 0,
    canBet: r.canBet !== false && x.canBet !== false,
    canCashout: r.canCashout === true || x.canCashout === true,
    recent: Array.isArray(x.recent) ? x.recent.filter((v) => !isObject(v)).map(Number).filter(Number.isFinite) : [],
    players: Array.isArray(x.players) ? x.players : [],
  };
};

const normalizeBet = (value) => {
  if (!value) return null;
  const x = isObject(value) && isObject(value.data) && !value._id ? value.data : value;
  return isObject(x) ? x : null;
};

const getUserKeys = (user) =>
  [user?._id, user?.id, user?.userId, user?.mobile, user?.phone, user?.username]
    .filter((v) => v !== undefined && v !== null && v !== '')
    .map(String);

const getBetUserKeys = (bet) =>
  [
    bet?.userId,
    bet?.user?._id,
    bet?.user?.id,
    bet?.user?.userId,
    bet?.user?.mobile,
    bet?.user?.phone,
    bet?.mobile,
    bet?.phone,
    bet?.username,
  ]
    .filter((v) => v !== undefined && v !== null && v !== '')
    .map(String);

const isOwnBet = (bet, user) => {
  if (!bet || !user) return false;
  const mine = getUserKeys(user);
  const owner = getBetUserKeys(bet);
  return mine.length > 0 && owner.some((value) => mine.includes(value));
};

const normalizeDetail = (raw) => {
  const x = unwrap(raw) || {};
  const round = normalizeRound(x.round || x.currentRound || x);
  const players = Array.isArray(x.bets) ? x.bets : (Array.isArray(x.players) ? x.players : []);
  const user = getStoredUser();
  const explicitBet = normalizeBet(
    x.userBet || x.myBet || x.currentUserBet || x.currentUserBetData
  );
  const matchedBet = explicitBet || players.find((bet) => isOwnBet(bet, user)) || null;
  return {
    ...round,
    players,
    myBet: normalizeBet(matchedBet),
  };
};

export default function useGame() {
  const [state, setState] = useState({
    status: 'WAITING', multiplier: 1, roundNumber: null, roundId: null,
    crashPoint: null, crashAt: 0, crashShownValue: 1, bettingEndTime: null, flightStartTime: null,
    startTime: null, remainingMilliseconds: 0, recent: [], players: [], myBet: null,
  });
  const [connected, setConnected] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, []);

  const lastEventAt = useRef(Date.now());

  // Crash detection that does not depend on a CRASH event: the moment the round
  // leaves FLYING, show "FLEW AWAY!" with the last multiplier that was flown.
  const prevStatus = useRef(null);
  const lastFlyMultiplier = useRef(1);
  useEffect(() => {
    if (state.status === 'FLYING') {
      lastFlyMultiplier.current = Math.max(lastFlyMultiplier.current, Number(state.multiplier) || 1);
    }
    const left = prevStatus.current === 'FLYING' && state.status !== 'FLYING';
    prevStatus.current = state.status;
    if (left && !(state.crashAt && Date.now() - state.crashAt < 1500)) {
      const value = Number(state.crashPoint) > 1 ? Number(state.crashPoint) : lastFlyMultiplier.current;
      setState((s) => ({ ...s, crashAt: Date.now(), crashShownValue: value }));
    }
    if (state.status !== 'FLYING' && state.status !== 'CRASHED') lastFlyMultiplier.current = 1;
  }, [state.status, state.multiplier, state.crashPoint, state.crashAt]);


  useEffect(() => {
    let mounted = true;
    const hydrate = async () => {
      try {
        const [roundRes, detailRes] = await Promise.all([getCurrentRoundWithBets()]);
        if (!mounted) return;
        const round = withBettingTiming(normalizeRound(roundRes?.data));
        const detail = normalizeDetail(detailRes?.data);

        // Some backend versions return the current round bets but omit the
        // dedicated `userBet` field. Fall back to bet history so a page reload
        // during FLYING can still restore the user's live bet and cash-out UI.
        let hydrated = detail;
        if (!detail.myBet && detail.roundId) {
          try {
            const historyRes = await getBetHistory(100);
            const history = unwrap(historyRes?.data) || {};
            const rows = Array.isArray(history)
              ? history
              : Array.isArray(history?.bets)
                ? history.bets
                : Array.isArray(history?.data)
                  ? history.data
                  : [];
            const current = rows.find((bet) => {
              const betRound = String(bet?.roundId ?? bet?.round?._id ?? bet?.round?.roundId ?? '');
              return betRound === String(detail.roundId) && isOwnBet(bet, getStoredUser()) &&
                !['WON', 'CASHED_OUT', 'CASHOUT', 'LOST', 'LOSS'].includes(String(bet?.status || '').toUpperCase());
            });
            if (current) hydrated = { ...detail, myBet: normalizeBet(current) };
          } catch (historyError) {
            console.warn('Unable to hydrate live bet from history:', historyError?.message || historyError);
          }
        }
        const timedDetail = withBettingTiming(hydrated);
        setState((s) => ({
          ...s, ...round, ...timedDetail,
          roundId: round.roundId || timedDetail.roundId || s.roundId,
          roundNumber: round.roundNumber ?? timedDetail.roundNumber ?? s.roundNumber,
          myBet: timedDetail.myBet || null,
        }));
      } catch (e) {
        console.warn('Unable to load current round:', e.message);
      }
    };
    hydrate();

    const socket = connectSocket();
    if (!socket) return () => { mounted = false; };

    const handler = {
      onConnected: () => setConnected(true),
      onGameState: (d) => setState((s) => {
        const payload = unwrap(d) || {};
        const normalized = normalizeDetail(payload);
        const status = String(normalized.status || '').toUpperCase();
        const timed = withBettingTiming(normalized);
        return { ...s, ...timed, status: BETTING_STATUSES.has(status) ? 'BETTING' : normalized.status || s.status, myBet: normalized.myBet || s.myBet };
      }),
      onRoundCreated: (d) => setState((s) => { const round = withBettingTiming({ ...normalizeRound(d), status: 'BETTING' }); return { ...s, ...round, status: 'BETTING', multiplier: 1, myBet: null }; }),
      onBetting: (d) => setState((s) => {
        const round = withBettingTiming({
          ...normalizeRound(d),
          status: 'BETTING',
        });

        const userBet = normalizeBet(
          d?.userBet ||
          d?.bet ||
          d?.data?.bet
        );

        const ownBet =
          userBet &&
          isOwnBet(userBet, getStoredUser())
            ? userBet
            : null;

        const betRoundId = String(
          userBet?.roundId ||
          d?.roundId ||
          ""
        );

        const currentRoundId = String(
          round?.roundId || ""
        );

        const sameRound =
          !betRoundId ||
          !currentRoundId ||
          betRoundId === currentRoundId;

        return {
          ...s,
          ...round,
          status: 'BETTING',
          multiplier: 1,

          // Never carry an old round bet into the new round.
          myBet: sameRound
            ? (ownBet || s.myBet)
            : null,
        };
      }),
      onFlightStarted: (d) => setState((s) => ({
        ...s, ...normalizeRound(d), status: 'FLYING', multiplier: Number(d?.multiplier || d?.currentMultiplier || 1), flightStartTime: d?.flightStartTime || d?.startTime || new Date().toISOString(),
      })),
      onMultiplier: (d) => setState((s) => ({ ...s, ...normalizeRound(d), status: 'FLYING', multiplier: Number(d?.multiplier || d?.currentMultiplier || s.multiplier || 1), flightStartTime: s.flightStartTime || d?.flightStartTime || d?.startTime || new Date().toISOString() })),
      onCrash: (d) => setState((s) => {
        const value = Number(d?.crashPoint || d?.multiplier || s.multiplier || 1);
        return {
          ...s, ...normalizeRound(d), status: 'CRASHED', multiplier: value, crashPoint: value,
          // Remember the crash so the "FLEW AWAY!" screen stays visible for a moment
          // even if the server immediately sends SETTLED / the next round.
          crashAt: Date.now(), crashShownValue: value,
        };
      }),
      onSettled: (d) => setState((s) => ({ ...s, ...normalizeRound(d), status: 'SETTLED' })),
      onBetPlaced: (d) => {
        const bet = normalizeBet(d?.bet || d?.data?.bet || d?.userBet);
        // The bet event can be broadcast to every connected player. Only hydrate
        // the local cash-out state when the event belongs to this logged-in user.
        if (bet && isOwnBet(bet, getStoredUser())) {
          setState((s) => ({ ...s, myBet: bet }));
        }
      },
      onCashout: (d) => {
        const bet = normalizeBet(d?.bet || d?.data?.bet || d?.userBet);
        const wallet = d?.wallet || d?.data?.wallet || d?.walletData || d?.data?.walletData;
        const ownBet = bet && isOwnBet(bet, getStoredUser());
        if (ownBet || wallet) setState((s) => ({
          ...s,
          myBet: ownBet ? { ...(s.myBet || {}), ...bet, cashedOut: true } : s.myBet,
          wallet: wallet || s.wallet,
        }));
      },
      onWalletUpdated: (data) => {
        // Backends may emit {wallet}, {data:{wallet}}, or the wallet object itself.
        const payload = data?.wallet || data?.data?.wallet || data?.walletData || data?.data || data;
        if (payload && typeof payload === 'object') setState((s) => ({ ...s, wallet: payload }));
      },
      onError: (data) => console.warn('Aviator socket error:', data?.message || data),
    };

    // Every live socket event marks "socket is alive" for the watchdog below.
    const wrapped = Object.fromEntries(
      Object.entries(handler).map(([name, fn]) => [name, (...args) => { lastEventAt.current = Date.now(); return fn(...args); }]),
    );
    registerSocketListeners(socket, wrapped);

    // After (re)connect, reload the current round so the UI never stays on a stale multiplier.
    const onSocketConnect = () => { lastEventAt.current = Date.now(); hydrate(); };
    socket.on('connect', onSocketConnect);
    const watchdog = setInterval(async () => {
      if (!mounted || document.hidden) return;
      if (socket.connected && Date.now() - lastEventAt.current < 1500) return;
      try {
        const res = await getCurrentRound();
        if (!mounted) return;
        // keep the existing recent/players lists; the round endpoint does not carry them
        const { recent, players, ...base } = normalizeRound(res?.data);
        const round = withBettingTiming(base);
        setState((s) => ({ ...s, ...round, roundId: round.roundId || s.roundId, myBet: s.myBet }));
      } catch (e) { /* next tick retries */ }
    }, 500);

    const disconnectHandler = () => setConnected(false);
    socket.on('disconnect', disconnectHandler);
    if (socket.connected) setConnected(true);
    return () => {
      mounted = false;
      clearInterval(watchdog);
      socket.off('connect', onSocketConnect);
      socket.off('disconnect', disconnectHandler);
      removeSocketListeners(socket);
      disconnectSocket();
    };
  }, []);

  const phase = useMemo(() => {
    const status = String(state.status || 'WAITING').toUpperCase();
    const end = toTimestamp(state.bettingEndTime);
    if (status === 'CRASHED') return 'CRASHED';
    if (status === 'SETTLED') return 'SETTLED';
    if (status === 'FLYING') return 'FLYING';
    if (['LOCKED', 'BETTING_CLOSED', 'PROCESSING', 'TAKEOFF'].includes(status)) return 'TAKEOFF';
    if (BETTING_STATUSES.has(status)) {
      // One continuous bar: 10s betting open + 2s processing (bets closed).
      // The server starts the flight 2s after bettingEndTime, so the bar runs
      // through that window instead of sitting empty.
      if (end && now >= end && now < end + TAKEOFF_MS) return 'TAKEOFF';
      // Flight begins only when the server confirms it.
      if (end && now >= end + TAKEOFF_MS) return 'WAITING';
      return 'BETTING';
    }
    return 'WAITING';
  }, [state.status, state.bettingEndTime, now]);

  const bettingDuration = useMemo(() => {
    const end = toTimestamp(state.bettingEndTime);
    const start = toTimestamp(state.startTime);
    if (end > start) return Math.max(1, (end - start) / 1000);
    return 10;
  }, [state.bettingEndTime, state.startTime]);

  const bettingSeconds = useMemo(() => {
    const end = toTimestamp(state.bettingEndTime);
    if (!end) return 10;
    return Math.max(0, Math.ceil((end - now) / 1000));
  }, [state.bettingEndTime, now]);

  const bettingProgress = useMemo(() => {
    // Bar covers betting (10s) + processing (2s) as one continuous line.
    const durationMs = Math.max(1000, bettingDuration * 1000) + TAKEOFF_MS;
    const end = toTimestamp(state.bettingEndTime);
    if (end) return Math.max(0, Math.min(1, (end + TAKEOFF_MS - now) / durationMs));
    return 1;
  }, [state.bettingEndTime, bettingDuration, now]);

  const takeoffProgress = useMemo(() => {
    const end = toTimestamp(state.bettingEndTime);
    if (!end) return 0;
    return Math.min(1, Math.max(0, (now - end) / TAKEOFF_MS));
  }, [state.bettingEndTime, now]);

  const flightElapsed = useMemo(() => {
    if (phase !== 'FLYING') return 0;
    const explicitStart = toTimestamp(state.flightStartTime);
    const bettingEnd = toTimestamp(state.bettingEndTime);
    const start = bettingEnd ? Math.max(explicitStart || 0, bettingEnd + TAKEOFF_MS) : (explicitStart || now);
    return Math.max(0, (now - start) / 1000);
  }, [phase, state.flightStartTime, state.bettingEndTime, now]);

  // View phase: keeps the crash screen on for CRASH_HOLD_MS (like the reference game),
  // while `phase` keeps following the real server state for betting logic.
  const boardPhase = useMemo(() => {
    // Crash screen lasts until the next betting round starts (never over the betting screen).
    if (phase !== 'FLYING' && !BETTING_STATUSES.has(String(state.status || '').toUpperCase()) &&
        state.crashAt && now - state.crashAt < CRASH_HOLD_MS) return 'CRASHED';
    return phase;
  }, [phase, state.status, state.crashAt, now]);

  return { ...state, connected, phase, boardPhase, bettingSeconds, bettingDuration, bettingProgress, takeoffProgress, flightElapsed, setState };
}
